import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from "../utils/supabase"
import {
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Typography,
	Box,
	Stack,
	Chip,
	CircularProgress,
	Grid
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BusinessIcon from '@mui/icons-material/Business'
import StoreIcon from '@mui/icons-material/Store'

dayjs.extend(utc)
dayjs.extend(timezone)

const ITEMS_PER_PAGE = 3

function UserDashboard() {
	const navigate = useNavigate()
	const [session, setSession] = useState(null)
	const [fullName, setFullName] = useState("")
	const [upcomingAppointments, setUpcomingAppointments] = useState([])
	const [loadingAppointments, setLoadingAppointments] = useState(false)
	const [hasMore, setHasMore] = useState(true)
	const [offset, setOffset] = useState(0)

	useEffect(() => {
		let mounted = true
		const init = async () => {
			const { data } = await supabase.auth.getSession()
			if (!mounted) return
			setSession(data.session)
			if (data.session?.user?.id) {
				const { data: profile } = await supabase
					.from('profiles')
					.select('full_name')
					.eq('id', data.session.user.id)
					.maybeSingle()
				if (profile?.full_name) setFullName(profile.full_name)
			}
		}
		init()
		return () => { mounted = false }
	}, [])

	// Load initial appointments when session is available
	useEffect(() => {
		if (session?.user?.id) {
			loadUpcomingAppointments(true) // true = reset
		}
	}, [session])

	const loadUpcomingAppointments = async (reset = false) => {
		if (!session?.user?.id) return

		setLoadingAppointments(true)
		try {
			const currentOffset = reset ? 0 : offset
			const now = dayjs().utc().toISOString()

			const { data, error } = await supabase
				.from('appointments')
				.select(`
					id,
					start_ts,
					end_ts,
					status,
					customer_name,
					customer_email,
					service_types!inner(name, duration_minutes),
					merchant!inner(name, timezone, slug, address, phone)
				`)
				.or(`user_id.eq.${session.user.id},and(user_id.is.null,customer_email.eq.${session.user.email})`)
				.eq('status', 'booked')
				.gte('start_ts', now)
				.order('start_ts', { ascending: true })
				.range(currentOffset, currentOffset + ITEMS_PER_PAGE - 1)

			if (error) throw error

			const appointmentsList = data || []

			if (reset) {
				setUpcomingAppointments(appointmentsList)
				setOffset(ITEMS_PER_PAGE)
			} else {
				setUpcomingAppointments(prev => [...prev, ...appointmentsList])
				setOffset(prev => prev + ITEMS_PER_PAGE)
			}

			// Check if there are more items
			setHasMore(appointmentsList.length === ITEMS_PER_PAGE)

		} catch (e) {
			console.error('Error loading appointments:', e)
			setUpcomingAppointments([])
		} finally {
			setLoadingAppointments(false)
		}
	}

	const loadMore = () => {
		loadUpcomingAppointments(false)
	}

	const formatAppointmentTime = (startTs, endTs, timezone) => {
		const start = dayjs(startTs).tz(timezone)
		const end = dayjs(endTs).tz(timezone)

		return {
			date: start.format('ddd, MMM D, YYYY'),
			time: `${start.format('h:mm A')} - ${end.format('h:mm A')}`
		}
	}

	const signOut = async () => {
		await supabase.auth.signOut()
		window.location.assign('/login')
	}

	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="w-full max-w-4xl space-y-6">
				{/* Main Dashboard Card */}
				<Card>
					<CardHeader
						title="User Dashboard"
						subheader={`Welcome${fullName ? `, ${fullName}` : session?.user?.email ? `, ${session.user.email}` : ''}.`}
					/>
					<CardContent>
						{/* Upcoming Appointments Section */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="h6" gutterBottom>
								Your Upcoming Appointments
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
								Your scheduled appointments
							</Typography>

							{loadingAppointments && upcomingAppointments.length === 0 ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
									<CircularProgress size={24} />
									<Typography sx={{ ml: 2 }}>Loading appointments...</Typography>
								</Box>
							) : upcomingAppointments.length === 0 ? (
								<Typography variant="body2" color="text.secondary">No upcoming appointments.</Typography>
							) : (
								<Stack spacing={2} sx={{ mb: 2 }}>
									{upcomingAppointments.map((appointment) => {
										const { date, time } = formatAppointmentTime(
											appointment.start_ts,
											appointment.end_ts,
											appointment.merchant.timezone
										)

										return (
											<Box
												key={appointment.id}
												sx={{
													p: 2,
													border: 1,
													borderColor: 'divider',
													borderRadius: 1,
													bgcolor: 'action.hover',
													cursor: 'pointer',
													'&:hover': {
														bgcolor: 'action.selected'
													}
												}}
												onClick={() => navigate(`/appointment/${appointment.id}`)}
											>
												<Grid container spacing={2}>
													{/* Store Info */}
													<Grid item xs={12} sm={6} md={3}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
															<StoreIcon fontSize="small" color="primary" />
															<Typography variant="subtitle2" fontWeight="bold">
																{appointment.merchant.name}
															</Typography>
														</Box>
														{appointment.merchant.address && (
															<Typography variant="caption" color="text.secondary">
																{appointment.merchant.address}
															</Typography>
														)}
													</Grid>

													{/* Service Info */}
													<Grid item xs={12} sm={6} md={3}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
															<BusinessIcon fontSize="small" color="action" />
															<Chip
																label={appointment.service_types.name}
																size="small"
																variant="outlined"
															/>
														</Box>
														<Typography variant="caption" color="text.secondary">
															{appointment.service_types.duration_minutes} minutes
														</Typography>
													</Grid>

													{/* Date Info */}
													<Grid item xs={12} sm={6} md={3}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
															<CalendarTodayIcon fontSize="small" color="action" />
															<Typography variant="body2" color="text.secondary">
																{date}
															</Typography>
														</Box>
													</Grid>

													{/* Time Info */}
													<Grid item xs={12} sm={6} md={3}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
															<AccessTimeIcon fontSize="small" color="action" />
															<Typography variant="body2" color="text.secondary">
																{time}
															</Typography>
														</Box>
														<Typography variant="caption" color="text.secondary">
															({appointment.merchant.timezone})
														</Typography>
													</Grid>
												</Grid>
											</Box>
										)
									})}
								</Stack>
							)}

							{/* Load More Button */}
							{hasMore && upcomingAppointments.length > 0 && (
								<Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
									<Button
										variant="outlined"
										onClick={loadMore}
										disabled={loadingAppointments}
										startIcon={loadingAppointments ? <CircularProgress size={20} /> : null}
									>
										{loadingAppointments ? 'Loading...' : 'Load More'}
									</Button>
								</Box>
							)}
						</Box>

						<Typography variant="body2" color="text.secondary">
							Click on any appointment to view details or find stores to book new appointments.
						</Typography>
					</CardContent>
					<CardActions>
						<Button className="ml-auto" variant="outlined" onClick={signOut}>Sign out</Button>
					</CardActions>
				</Card>
			</div>
		</div>
	)
}

export default UserDashboard 